// Type-only: this module stays import-free at runtime so its pure helpers can
// be unit-tested without pulling in the fragments/three runtime.
import type * as FRAGS from '@thatopen/fragments';

/**
 * Grouping an IFC model by property-set VALUES, for models whose spatial tree
 * carries no usable structure.
 *
 * Plenty of real exports — Civil 3D via EDM being the reference case — put
 * every element in one flat `IfcBuilding`, as the same `IfcBuildingElementProxy`
 * class, with `Name`, `ObjectType` and `Tag` all null. The spatial tree is then
 * N identical rows and the IFC-class filter offers one class: neither of the
 * export dialog's selection mechanisms can express "the asphalt course".
 *
 * What such files DO carry is a property set per element (`RBR-Data.RBR-Type_number`,
 * `Pset_…`, a discipline's own schema…) whose values partition the model exactly
 * the way the authoring tool's CAD layers do. So: scan the psets once, offer the
 * properties whose value count looks like a grouping, and let the user tick values.
 * Each ticked value becomes an export group — its own LandXML file and layer.
 */

/** Chunk size for `getItemsData` — ThatOpen's worker chokes on huge batches. */
const CHUNK = 500;

/**
 * `getItemsData` config: default attributes (we need `_guid`) plus the
 * `IsDefinedBy` relation, which is where fragments hangs the property sets.
 *
 * `relations: true` on `IsDefinedBy` — and the explicit `HasProperties` entry —
 * are load-bearing, not belt-and-braces: in IFC2X3 a pset's values live one
 * level down, as `IfcPropertySingleValue` children under `HasProperties`.
 * Fetching only the pset's own attributes returns its `Name` and nothing else,
 * and the whole picker comes up empty. This mirrors `ITEMS_DATA_CONFIG` in
 * `IFC2.tsx`, which is the config the element properties drawer actually
 * renders from. `relationsDefault` stays off: turning relations on globally
 * walks the entire IFC graph and freezes the tab.
 */
const DATA_CONFIG: Partial<FRAGS.ItemsDataConfig> = {
  attributesDefault: true,
  relationsDefault: { attributes: false, relations: false },
  relations: {
    IsDefinedBy: { attributes: true, relations: true },
    HasProperties: { attributes: true, relations: true },
  },
};

/**
 * Attributes holding an IFC property's value, in preference order. Their
 * presence is also what distinguishes a property LEAF (`IfcPropertySingleValue`)
 * from a container (`IfcPropertySet`) — see `walkProperties`.
 */
const VALUE_KEYS = [
  'NominalValue',
  'Value',
  'LengthValue',
  'AreaValue',
  'VolumeValue',
  'CountValue',
  'WeightValue',
  'TimeValue',
];

/** Bookkeeping attributes that are never a grouping key. */
const IGNORED_KEYS = new Set(['Name', 'Description', 'Unit', 'GlobalId', 'OwnerHistory', 'Identifier', 'Tag']);

/** How deep to follow relation arrays below `IsDefinedBy`. */
const MAX_PSET_DEPTH = 3;

/**
 * Categories that are never an exportable product.
 *
 * `getItemsOfCategories` with a match-everything regex returns EVERY indexed
 * entity, not just
 * elements: the reference model answers with 17,322 items for 443 proxies,
 * the rest being `IfcPropertySet` / `IfcPropertySingleValue` rows (which carry
 * GlobalIds of their own, so they can't be filtered by "has a GUID" either).
 * Scanning them costs a 39× longer fetch and contributes nothing — they have
 * no `IsDefinedBy` of their own.
 */
const NON_PRODUCT_CATEGORY =
  /^IFC(PROPERTY|QUANTITY|PHYSICALQUANTITY|UNIT|SIUNIT|DERIVEDUNIT|CONVERSIONBASEDUNIT|MONETARYUNIT|OWNERHISTORY|PERSON|ORGANIZATION|APPLICATION|PROJECT|MATERIAL|PRESENTATION|STYLED|SURFACESTYLE|CURVESTYLE|COLOUR|REL)/;

/**
 * The result of scanning a model's property sets.
 *
 * Elements are referred to by ORDINAL (their index in `guids`) rather than by
 * repeating the GUID string in every value bucket: a 100k-element model with 50
 * properties would otherwise hold five million copies of the same 22 characters.
 */
export interface PropertyScan {
  /** GUID of every scanned element that had one, in scan order. */
  guids: string[];
  /** `"<Pset>.<Property>"` → property value → ordinals into `guids`. */
  values: Map<string, Map<string, number[]>>;
  /** Elements actually scanned (≤ the model's item count). */
  scanned: number;
}

/** One property offered as a grouping key. */
export interface PropertyCandidate {
  /** `"<Pset>.<Property>"`. */
  key: string;
  /** Number of distinct values. */
  distinct: number;
  /** Elements carrying the property. */
  covered: number;
}

/** One export group: a ticked value and the elements that carry it. */
export interface PropertyGroup {
  value: string;
  guids: string[];
}

export const emptyScan = (): PropertyScan => ({ guids: [], values: new Map(), scanned: 0 });

/** Read one attribute as a trimmed, non-empty string (or undefined). */
function attrString(data: FRAGS.ItemData, key: string): string | undefined {
  const v = (data[key] as FRAGS.ItemAttribute | undefined)?.value;
  if (typeof v !== 'string') return undefined;
  const trimmed = v.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Render a property value as the string we group by.
 *
 * Numbers and booleans are stringified so a numeric code (`RBR-OCC: 1045`) and
 * a text one group alike; everything else (objects, arrays, null) is ignored —
 * those are nested IFC entities, not labels a user can pick from.
 */
function valueString(v: unknown): string | undefined {
  if (typeof v === 'string') {
    const t = v.trim();
    return t ? t : undefined;
  }
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : undefined;
  if (typeof v === 'boolean') return String(v);
  return undefined;
}

/** An IFC property's value, if this node is a property leaf. */
function leafValue(node: FRAGS.ItemData): string | undefined {
  for (const k of VALUE_KEYS) {
    const v = valueString((node[k] as FRAGS.ItemAttribute | undefined)?.value);
    if (v !== undefined) return v;
  }
  return undefined;
}

/**
 * Walk one `IsDefinedBy` entry and emit every `(pset, property, value)` it
 * holds. Deliberately shape-tolerant, because what fragments hands back
 * depends on the IFC schema and the authoring tool:
 *
 * - **Nested** (IFC2X3 / IFC4, the normal case): the pset carries a
 *   `HasProperties` array of `IfcPropertySingleValue` children, each with a
 *   `Name` and a `NominalValue`. A node is a property LEAF exactly when it has
 *   one of `VALUE_KEYS` — that test is what keeps a pset (which has none) from
 *   being mistaken for a single property.
 * - **Flat**: some models expose the properties as plain attributes of the
 *   pset itself. Then the pset is a container and every scalar attribute on it
 *   is a property.
 * - **Wrapped**: an `IfcRelDefinesByProperties` with the pset one level down.
 *   Handled for free by recursing into whatever arrays a node has.
 */
function walkProperties(
  node: FRAGS.ItemData,
  psetName: string | undefined,
  depth: number,
  emit: (pset: string | undefined, prop: string, value: string) => void,
  visited: Set<number> = new Set(),
): void {
  // The graph really is circular: a pset's `DefinesOcurrence` points straight
  // back at the element it was reached from. Without this guard the walk
  // re-enters the element and emits its own attributes as if they were
  // properties of the pset.
  const localId = (node._localId as FRAGS.ItemAttribute | undefined)?.value;
  if (typeof localId === 'number') {
    if (visited.has(localId)) return;
    visited.add(localId);
  }

  const own = attrString(node, 'Name');

  const direct = leafValue(node);
  if (direct !== undefined && own) {
    emit(psetName, own, direct);
    return;
  }

  // Container: recurse into relation arrays, and take its own scalars as
  // properties (the flat shape).
  const name = own ?? psetName;
  for (const key of Object.keys(node)) {
    if (key.startsWith('_')) continue;
    const raw = node[key];
    if (Array.isArray(raw)) {
      if (depth >= MAX_PSET_DEPTH) continue;
      for (const child of raw as FRAGS.ItemData[]) {
        if (child) walkProperties(child, name, depth + 1, emit, visited);
      }
      continue;
    }
    if (IGNORED_KEYS.has(key)) continue;
    const value = valueString((raw as FRAGS.ItemAttribute | undefined)?.value);
    if (value !== undefined) emit(name, key, value);
  }
}

/**
 * Fold one element's data into `scan`. Exported for tests: it is the whole
 * parsing contract (what counts as a pset, a property, a value) with none of
 * the fragments plumbing.
 */
export function accumulateItem(scan: PropertyScan, data: FRAGS.ItemData | undefined | null): void {
  if (!data) return;
  scan.scanned += 1;
  const guid = attrString(data, '_guid');
  // No GUID ⇒ nothing we could ever export; don't let it occupy an ordinal.
  if (!guid) return;
  const ordinal = scan.guids.length;
  scan.guids.push(guid);

  const psets = data.IsDefinedBy as FRAGS.ItemData[] | undefined;
  if (!Array.isArray(psets)) return;

  const emit = (pset: string | undefined, prop: string, value: string) => {
    const key = pset ? `${pset}.${prop}` : prop;
    let byValue = scan.values.get(key);
    if (!byValue) {
      byValue = new Map();
      scan.values.set(key, byValue);
    }
    const bucket = byValue.get(value);
    if (bucket) {
      // An element can hold the same property in two psets of the same name;
      // it must not be exported twice.
      if (bucket[bucket.length - 1] !== ordinal) bucket.push(ordinal);
    } else {
      byValue.set(value, [ordinal]);
    }
  };

  // The element itself is "visited" up front: the psets' `DefinesOcurrence`
  // inverse points back here, and a fresh set per pset would let each one walk
  // back into it.
  const visited = new Set<number>();
  const ownId = (data._localId as FRAGS.ItemAttribute | undefined)?.value;
  if (typeof ownId === 'number') visited.add(ownId);
  for (const pset of psets) {
    if (pset) walkProperties(pset, undefined, 0, emit, visited);
  }
}

/**
 * Properties worth offering as a grouping key, best first.
 *
 * Filtered: a single distinct value groups nothing, and more values than
 * `maxGroups` is an identifier (`RBR-Object_ID`, one per element), not a
 * grouping — the server caps the fan-out at the same number.
 *
 * Ordered by coverage (a property only half the model carries splits it badly),
 * then by fewest values, then by name so the list is stable across scans.
 */
export function candidateProperties(scan: PropertyScan, maxGroups: number): PropertyCandidate[] {
  const out: PropertyCandidate[] = [];
  for (const [key, byValue] of scan.values) {
    const distinct = byValue.size;
    if (distinct < 2 || distinct > maxGroups) continue;
    let covered = 0;
    for (const ordinals of byValue.values()) covered += ordinals.length;
    out.push({ key, distinct, covered });
  }
  return out.sort((a, b) => b.covered - a.covered || a.distinct - b.distinct || a.key.localeCompare(b.key));
}

/** Values of one property with their element counts, largest group first. */
export function propertyValues(scan: PropertyScan, key: string): { value: string; count: number }[] {
  const byValue = scan.values.get(key);
  if (!byValue) return [];
  return [...byValue.entries()]
    .map(([value, ordinals]) => ({ value, count: ordinals.length }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

/**
 * Resolve ticked values into export groups (name + GUIDs), in the order
 * `propertyValues` lists them so the jobs come out in a predictable order.
 * Values with no elements are dropped — an empty group would be a job that
 * exports the whole model.
 */
export function buildGroups(scan: PropertyScan, key: string, selected: ReadonlySet<string>): PropertyGroup[] {
  const byValue = scan.values.get(key);
  if (!byValue) return [];
  const groups: PropertyGroup[] = [];
  for (const { value } of propertyValues(scan, key)) {
    if (!selected.has(value)) continue;
    const guids = (byValue.get(value) ?? []).map((o) => scan.guids[o]).filter(Boolean);
    if (guids.length > 0) groups.push({ value, guids });
  }
  return groups;
}

/**
 * Scan every element of a fragment model for property sets.
 *
 * Unlike `resolveGuids` — where an all-or-nothing contract protects an export
 * from silently narrowing — a failed chunk here only costs grouping options, so
 * the walk continues and reports what it got. The caller still validates the
 * groups it builds.
 */
export async function scanPropertySets(
  model: FRAGS.FragmentsModel,
  options: { isCancelled?: () => boolean; onProgress?: (scanned: number, total: number) => void } = {},
): Promise<PropertyScan> {
  const scan = emptyScan();
  // A single regex matches every category — same trick `IFC2.tsx` uses for
  // colour-by-class. Gives us every item that has geometry to export.
  const byCategory = await model.getItemsOfCategories([/.*/]);
  const localIds: number[] = [];
  for (const [category, ids] of Object.entries(byCategory)) {
    if (!Array.isArray(ids)) continue;
    if (NON_PRODUCT_CATEGORY.test(category.toUpperCase())) continue;
    localIds.push(...ids);
  }
  const total = localIds.length;
  options.onProgress?.(0, total);

  for (let i = 0; i < total; i += CHUNK) {
    if (options.isCancelled?.()) break;
    const chunk = localIds.slice(i, i + CHUNK);
    try {
      const datas = (await model.getItemsData(chunk, DATA_CONFIG)) ?? [];
      for (const d of datas) accumulateItem(scan, d);
    } catch (err) {
      console.warn('[fragmentPropertyGroups] getItemsData failed', err);
    }
    options.onProgress?.(Math.min(i + CHUNK, total), total);
  }
  if (scan.values.size === 0 && total > 0) {
    // Nothing at all came back: almost always a property shape `walkProperties`
    // does not recognise. Dump one raw element so the next report can say what
    // it actually looks like instead of "the picker is empty".
    try {
      const [sample] = (await model.getItemsData(localIds.slice(0, 1), DATA_CONFIG)) ?? [];
      console.debug('[fragmentPropertyGroups] no properties found; sample item =', sample);
    } catch {
      /* diagnostics only */
    }
  }
  return scan;
}
